import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Text,
  Flex,
  Avatar,
  IconButton,
  useToast,
  Grid,
} from '@chakra-ui/react';
import { getAuth, signOut, verifyBeforeUpdateEmail } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { InfluencerDataContext } from '../../contexts/InfluencerDataContext';
import { ArrowBackIcon } from '@chakra-ui/icons';
import useCrisp from '../../hooks/useCrisp';
import FrostedSupportButton from '../../components/FrostedSupportButton';
import { MultiValue } from 'react-select';
import { Bundle, RateRanges } from '@foodfluence/shared';
import { InfluencerLocationContext } from '../../contexts/InfluencerLocationContext';
import { AssociatedTagsContext } from '../../contexts/AssociatedTagsContext';
import { useInstagramVerificationStatus } from '../../hooks/useInstagramVerification';
import { getInfluencerDisplayName, hasValidInstagramHandle, hasValidTikTokHandle } from '../../utils/influencerUtils';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { isFoodfluenceEmail } from '../../utils/authUtils';
import SwitchAccountModal from './components/SwitchAccountModal';
import { parsePhone } from './utils';
import { InfluencerLocation } from '@foodfluence/shared';
import { useTabBarVisibility } from '../../contexts/TabBarVisibilityContext';
import { CollabDataContext } from '../../contexts/collabDataContext';
import { InfluencerCampaignDataContext } from '../../contexts/InfluencerCampaignDataContext';
import { useBountyData } from '../../contexts/BountyDataContext';
import { isRetailCampaignType, isShippingCampaignType } from '../../utils/collabUtils';
import { formatEarningsTile, getTotalBountyEarnings } from '../../utils/earningsUtils';
import ProfileWhopWalletCard from '../wallet/components/ProfileWhopWalletCard';
import WalletPageWithPayoutPortal from '../wallet/WalletPageWithPayoutPortal';
import WalletPayoutIntroModal from '../wallet/components/WalletPayoutIntroModal';
import { useWalletPayoutIntro } from '../wallet/hooks/useWalletPayoutIntro';
import DiamondTierModal from './components/diamond/DiamondTierModal';
import DiamondAvatarBadge from './components/diamond/DiamondAvatarBadge';
import { useDiamondCelebration } from './components/diamond/useDiamondCelebration';
import { walletApi } from '../../services/api/wallet';
import { useWhopPayoutsModal } from '../wallet/contexts/WhopPayoutsModalContext';
import { openKycPanelAfterSetup } from '../wallet/utils/walletSetupPayoutsUtils';
import { influencerApi } from '../../services/api/influencer';
import { doesRecentInstagramVerificationCodeExist } from '../../services/firebaseService';
import { useInstagramVerification } from '../../contexts/InstagramVerificationContext';
import { walletColors } from '../wallet/walletTheme';
import { ff, navClearance } from '../../theme';
import { getProfileCompleteness } from './profileCompleteness';

// Import section-specific edit components
import NichesEdit from './components/NichesEdit';
import PersonalInformationEdit from './components/PersonalInformationEdit';
import SocialsEdit from './components/SocialsEdit';
import LocationsEdit from './components/LocationsEdit';
import CollaborationPreferencesEdit from './components/CollaborationPreferencesEdit';
import CancelChangesModal from './components/EditProfileComponents/CancelChangesModal';
import RatesEdit from './components/RatesEdit';
import MediaKitEdit from './components/MediaKitEdit';
import NotificationsEdit from './components/NotificationsEdit';
import UgcEdit from './components/UgcEdit';
import LocalCreatorsEdit from './components/LocalCreatorsEdit';
import LocalCreatorsBannerLogo from './components/localCreators/components/LocalCreatorsBannerLogo';
import LocalCreatorsBrandMark from './components/localCreators/components/LocalCreatorsBrandMark';
const DiamondGlyph: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6.5 4h11l4 5.5L12 21 2.5 9.5z" fill={ff.lime} />
    <path d="M2.5 9.5h19M9 4l-1.5 5.5L12 21l4.5-11.5L15 4" stroke={ff.ink} strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);

const ProfileGroup: React.FC<{ title: string; last?: boolean; children: React.ReactNode }> = ({ title, last, children }) => (
  <Box
    as="section"
    display="flex"
    flexDirection="column"
    sx={last ? { '& > button:last-of-type': { borderBottom: `1px solid ${ff.line}` } } : undefined}
  >
    <Text as="h2" m={0} mb="6px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase">
      {title}
    </Text>
    {children}
  </Box>
);

/**
 * One settings row. `value` is the current state; when it is empty and `action` is set, the
 * row shows a lime call-to-action pill instead (the board's "Add" / "Set" / "Upload").
 */
const ProfileRow: React.FC<{
  icon: React.ReactNode;
  title: string;
  value?: string;
  action?: string;
  onClick: () => void;
}> = ({ icon, title, value, action, onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    gap="14px"
    minH="64px"
    w="100%"
    p={0}
    borderTop="1px solid"
    borderColor={ff.line}
    bg="transparent"
    color={ff.ink}
    textAlign="left"
  >
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icon}
    </svg>
    <Text as="span" flex="1" fontSize="16px" fontWeight={600} whiteSpace="nowrap">
      {title}
    </Text>
    {value ? (
      <Text as="span" fontSize="14px" color={ff.text2} noOfLines={1} maxW="50%" textAlign="right">
        {value}
      </Text>
    ) : action ? (
      <Flex as="span" flex="none" h="26px" px="10px" borderRadius="full" bg={ff.lime} align="center" fontSize="12px" fontWeight={600}>
        {action}
      </Flex>
    ) : null}
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ff.muted} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  </Flex>
);

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [user] = useAuthState(auth);
  const { influencerData, switchToInfluencer, resetToOwnAccount, isSwitchedAccount } = useContext(InfluencerDataContext);
  const { openCrisp } = useCrisp(influencerData?.email_address);
  const { influencerLocations } = useContext(InfluencerLocationContext);
  const { associatedTags: associatedTagsContext } = useContext(AssociatedTagsContext);
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(() => {
    const state = location.state as { section?: string } | null;
    return state?.section ?? null;
  });
  const { setHideTabBar } = useTabBarVisibility();

  useEffect(() => {
    setHideTabBar(!!editingSection);
    return () => {
      setHideTabBar(false);
    };
  }, [editingSection, setHideTabBar]);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const { claims } = useBountyData();

  const { isVerified: isInstagramVerified, isLoading: isCheckingInstagramVerified } =
    useInstagramVerificationStatus(influencerData?.instagramHandle, !!influencerData?.instagramVerifiedAt);
  const {
    isVerified: isPendingHandleVerified,
    hasOwnershipConflict: hasHandleOwnershipConflict,
    watchInstagramHandle,
  } = useInstagramVerification();
  const canSwitchAccount = isFoodfluenceEmail(user?.email || null);

  // Staff viewing a switched-into creator are looking at that creator's record, and
  // dismissing would write the seen flag to *their* doc — burning a one-time celebration
  // the creator never saw.
  const canShowDiamondCelebration = !editingSection && !isSwitchedAccount;

  const { isCelebrationOpen, isCelebrationPending, dismissCelebration, openDiamondModal } = useDiamondCelebration({
    enabled: canShowDiamondCelebration,
  });

  const { openPayoutsPanel } = useWhopPayoutsModal();

  // Staff switched into a creator must not run Whop setup/KYC — that binds the staff session's
  // identity to the creator's payout account (same rule as blocking email change while switched).
  const canShowWalletPayoutIntro = !editingSection && !isCelebrationPending && !isSwitchedAccount;

  const { isIntroOpen, isIntroSetupLoading, dismissIntro, handleIntroSetup } = useWalletPayoutIntro({
    // Both can qualify on the same render; the Diamond celebration goes first. Gate on pending
    // rather than isCelebrationOpen — that only flips in an effect, a render later, so the
    // wallet modal would mount and flash before being closed.
    enabled: canShowWalletPayoutIntro,
    onSetup: async () => {
      if (isSwitchedAccount) {
        return;
      }
      const influencerId = influencerData?.id;
      if (!influencerId || influencerData?.whopCompanyId) {
        return;
      }
      const result = await walletApi.setupAccount({ influencerId });
      try {
        await openKycPanelAfterSetup({
          influencerId,
          companyId: result.companyId,
          kycApproved: result.kycApproved,
          setKycApproved: () => {},
          onWithdrawalComplete: () => {},
          openPayoutsPanel,
        });
      } catch {
        // Account exists; user can verify from wallet.
      }
    },
  });

  // Edit state
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email_address, setEmail_address] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [birthday, setBirthday] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  const [pronouns, setPronouns] = useState<MultiValue<{ value: string; label: string }>>([]);
  const [ethnicity, setEthnicity] = useState<string>('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [locations, setLocations] = useState<InfluencerLocation[]>([]);
  const [instagramHandle, setInstagramHandle] = useState<string>('');
  // Kept out of the influencerData re-seed effect below on purpose: that effect runs on every
  // realtime snapshot and would wipe what the creator is part-way through typing.
  const [newInstagramHandle, setNewInstagramHandle] = useState<string>('');
  const [isRequestingHandleChange, setIsRequestingHandleChange] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const [isRequestingEmailChange, setIsRequestingEmailChange] = useState<boolean>(false);
  const [tiktokHandle, setTiktokHandle] = useState<string>('');
  const [youtubeHandle, setYoutubeHandle] = useState<string>('');
  const [pinterestHandle, setPinterestHandle] = useState<string>('');
  const [snapchatHandle, setSnapchatHandle] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [collabPreference, setCollabPreference] = useState<string>('Open to all collaboration offers');
  const [rateRanges, setRateRanges] = useState<RateRanges>({
    gifting: { 
      minRate: influencerData?.rateRanges?.gifting?.minRate || 0, 
    },
    instagramPosts: { 
      offered: influencerData?.rateRanges?.instagramPosts?.offered ?? hasValidInstagramHandle(influencerData),
      minRate: influencerData?.rateRanges?.instagramPosts?.minRate || 0, 
    },
    instagramReels: { 
      offered: influencerData?.rateRanges?.instagramReels?.offered ?? hasValidInstagramHandle(influencerData),
      minRate: influencerData?.rateRanges?.instagramReels?.minRate || 0, 
    },
    instagramStories: { 
      offered: influencerData?.rateRanges?.instagramStories?.offered ?? hasValidInstagramHandle(influencerData),
      minRate: influencerData?.rateRanges?.instagramStories?.minRate || 0, 
    },
    tiktoks: { 
      offered: influencerData?.rateRanges?.tiktoks?.offered ?? !!influencerData?.tiktokHandle,
      minRate: influencerData?.rateRanges?.tiktoks?.minRate || 0, 
    },
  });
  const [bundlePricing, setBundlePricing] = useState<Bundle[]>([]);
  const [mediaKit, setMediaKit] = useState<string>('');
  
  // Track last saved values for autosave functionality
  const [lastSavedPersonalInfo, setLastSavedPersonalInfo] = useState<{
    firstName: string;
    lastName: string;
    phoneNumber: string;
    countryCode: string;
    birthday: string;
    gender: string;
    pronouns: { value: string; label: string }[];
    ethnicity: string;
    languages: string[];
  } | null>(null);
  
  const [lastSavedLocationsInfo, setLastSavedLocationsInfo] = useState<{
    locations: InfluencerLocation[];
    defaultShippingAddress: string | null;
  } | null>(null);
  
  const [lastSavedSocialsInfo, setLastSavedSocialsInfo] = useState<{
    tiktokHandle: string;
    youtubeHandle: string;
    pinterestHandle: string;
    snapchatHandle: string;
  } | null>(null);
  
  const [lastSavedNichesInfo, setLastSavedNichesInfo] = useState<{
    selectedTags: string[];
  } | null>(null);
  
  const [lastSavedCollabPreferences, setLastSavedCollabPreferences] = useState<{
    collabPreference: string;
  } | null>(null);
  
  const [lastSavedRatesInfo, setLastSavedRatesInfo] = useState<{
    rateRanges: RateRanges;
    bundlePricing: Bundle[];
  } | null>(null);
  
  const [lastSavedMediaKit, setLastSavedMediaKit] = useState<string | null>(null);

  // Initialize form data from influencerData
  useEffect(() => {
    if (influencerData) {
      const initialPhone = parsePhone(influencerData?.phoneNumber || '');
      setFirstName((!influencerData?.firstName || influencerData?.firstName.toLowerCase() === 'unknown') ? '' : influencerData?.firstName);
      setLastName((!influencerData?.lastName || influencerData?.lastName.toLowerCase() === 'unknown') ? '' : influencerData?.lastName);
      setEmail_address(influencerData?.email_address || '');
      setPhoneNumber(influencerData?.phoneNumber || '');
      setCountryCode(initialPhone.countryCode);
      setBirthday(influencerData?.birthday || '');
      setEthnicity(influencerData?.ethnicity || '');
      setGender(influencerData?.gender || '');
      setPronouns((influencerData?.pronouns || []).map(p => ({ value: p, label: p })));
      setLanguages(influencerData?.languages || []);
      setInstagramHandle(influencerData?.instagramHandle || '');
      setTiktokHandle(influencerData?.tiktokHandle || '');
      setYoutubeHandle(influencerData?.youtubeHandle || '');
      setPinterestHandle(influencerData?.pinterestHandle || '');
      setSnapchatHandle(influencerData?.snapchatHandle || '');
      setSelectedTags(influencerData?.associatedTags || []);
      setCollabPreference(influencerData?.collabPreference || 'Open to all collaboration offers');
      setRateRanges({
        gifting: {
          minRate: influencerData?.rateRanges?.gifting?.minRate || 0,
        },
        instagramReels: {
          offered: influencerData?.rateRanges?.instagramReels?.offered ?? hasValidInstagramHandle(influencerData),
          minRate: influencerData?.rateRanges?.instagramReels?.minRate || 0,
        },
        instagramPosts: {
          offered: influencerData?.rateRanges?.instagramPosts?.offered ?? hasValidInstagramHandle(influencerData),
          minRate: influencerData?.rateRanges?.instagramPosts?.minRate || 0,
        },
        instagramStories: {
          offered: influencerData?.rateRanges?.instagramStories?.offered ?? hasValidInstagramHandle(influencerData),
          minRate: influencerData?.rateRanges?.instagramStories?.minRate || 0,
        },
        tiktoks: {
          offered: influencerData?.rateRanges?.tiktoks?.offered ?? hasValidTikTokHandle(influencerData),
          minRate: influencerData?.rateRanges?.tiktoks?.minRate || 0,
        },
      });
      setBundlePricing(influencerData?.bundlePricing || []);
      setMediaKit(influencerData?.mediaKit || '');
      if (editingSection !== 'locations') {
        if (influencerLocations && influencerLocations.length > 0) {
          setLocations(influencerLocations);
        } else {
          setLocations([]);
        }
      }
      
      // Initialize last saved values for autosave
      setLastSavedPersonalInfo({
        firstName: (!influencerData?.firstName || influencerData?.firstName.toLowerCase() === 'unknown') ? '' : influencerData?.firstName,
        lastName: (!influencerData?.lastName || influencerData?.lastName.toLowerCase() === 'unknown') ? '' : influencerData?.lastName,
        phoneNumber: influencerData?.phoneNumber || '',
        countryCode: initialPhone.countryCode,
        birthday: influencerData?.birthday || '',
        gender: influencerData?.gender || '',
        pronouns: (influencerData?.pronouns || []).map(p => ({ value: p, label: p })),
        ethnicity: influencerData?.ethnicity || '',
        languages: influencerData?.languages || [],
      });
      
      setLastSavedLocationsInfo({
        locations: influencerLocations || [],
        defaultShippingAddress: null,
      });
      
      setLastSavedSocialsInfo({
        tiktokHandle: influencerData?.tiktokHandle || '',
        youtubeHandle: influencerData?.youtubeHandle || '',
        pinterestHandle: influencerData?.pinterestHandle || '',
        snapchatHandle: influencerData?.snapchatHandle || '',
      });
      
      setLastSavedNichesInfo({
        selectedTags: influencerData?.associatedTags || [],
      });
      
      setLastSavedCollabPreferences({
        collabPreference: influencerData?.collabPreference || 'Open to all collaboration offers',
      });
      
      setLastSavedRatesInfo({
        rateRanges: {
          gifting: {
            minRate: influencerData?.rateRanges?.gifting?.minRate || 0,
          },
          instagramReels: {
            offered: influencerData?.rateRanges?.instagramReels?.offered ?? hasValidInstagramHandle(influencerData),
            minRate: influencerData?.rateRanges?.instagramReels?.minRate || 0,
          },
          instagramPosts: {
            offered: influencerData?.rateRanges?.instagramPosts?.offered ?? hasValidInstagramHandle(influencerData),
            minRate: influencerData?.rateRanges?.instagramPosts?.minRate || 0,
          },
          instagramStories: {
            offered: influencerData?.rateRanges?.instagramStories?.offered ?? hasValidInstagramHandle(influencerData),
            minRate: influencerData?.rateRanges?.instagramStories?.minRate || 0,
          },
          tiktoks: {
            offered: influencerData?.rateRanges?.tiktoks?.offered ?? hasValidTikTokHandle(influencerData),
            minRate: influencerData?.rateRanges?.tiktoks?.minRate || 0,
          },
        },
        bundlePricing: influencerData?.bundlePricing || [],
      });
      
      setLastSavedMediaKit(influencerData?.mediaKit || '');
    }
  }, [influencerData, influencerLocations, editingSection]);

  // Scroll to top when editing section changes
  useEffect(() => {
    if (editingSection) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [editingSection]);

  const handleSignOut = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const pendingInstagramHandle = influencerData?.pendingInstagramHandle;
  // The Instagram DM webhook rejects codes older than 10 minutes, so the pending state
  // expires with the code rather than lingering forever.
  const pendingHandleExpiresAt = influencerData?.pendingInstagramHandleRequestedAt
    ? influencerData.pendingInstagramHandleRequestedAt.seconds * 1000 + 10 * 60 * 1000
    : undefined;

  const goToInstagramVerification = (handle: string, code?: string) => {
    navigate('/instagram-verification', {
      state: {
        email: influencerData?.email_address,
        instagram: handle,
        phoneNumber: influencerData?.phoneNumber,
        influencerID: influencerData?.id,
        code,
        returnTo: '/profile',
        returnState: { section: 'socials' }
      }
    });
  };

  const handleVerifyInstagram = async () => {
    if (pendingInstagramHandle) {
      // Carry the existing code through. Without it the verification screen would create a
      // fresh record for this handle, dropping the creator ID the backend stamped on it —
      // and an unstamped record can never be promoted.
      const existingCode = await doesRecentInstagramVerificationCodeExist(pendingInstagramHandle)
        .catch(() => null);
      if (!existingCode) {
        toast({
          title: 'That verification code expired',
          description: 'Cancel the change and start again to get a new code',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      goToInstagramVerification(pendingInstagramHandle, existingCode);
      return;
    }

    // No pending change: this is the legacy "prove the handle you already have" path, which
    // creates its own record client-side exactly as signup does.
    if (influencerData?.instagramHandle) {
      goToInstagramVerification(influencerData.instagramHandle);
    }
  };

  /**
   * Starts a handle change. The live handle is untouched until the new one is proven, so a
   * creator part-way through this still shows brands the handle they have actually proven.
   */
  const handleRequestInstagramHandleChange = async () => {
    if (!influencerData?.id || !newInstagramHandle.trim()) return;

    setIsRequestingHandleChange(true);
    try {
      const { handle, verificationCode } = await influencerApi.requestInstagramHandleChange(
        newInstagramHandle,
        influencerData.id
      );
      setNewInstagramHandle('');
      goToInstagramVerification(handle, verificationCode);
    } catch (error) {
      toast({
        title: "Couldn't start the handle change",
        description: error instanceof Error ? error.message : 'Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRequestingHandleChange(false);
    }
  };

  const handleCancelInstagramHandleChange = async () => {
    if (!influencerData?.id) return;
    try {
      await influencerApi.cancelInstagramHandleChange(influencerData.id);
    } catch (error) {
      toast({
        title: "Couldn't cancel the handle change",
        description: error instanceof Error ? error.message : 'Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Watch the pending handle's proof record, scoped to this creator so a handle someone else
   * once proved cannot promote it.
   */
  useEffect(() => {
    if (pendingInstagramHandle && influencerData?.id) {
      watchInstagramHandle(pendingInstagramHandle, influencerData.id);
    }
  }, [pendingInstagramHandle, influencerData?.id, watchInstagramHandle]);

  /**
   * Ask the backend to promote the pending handle. Runs whenever a pending handle is present
   * and again when the proof record flips verified, so a creator who DMs the code and closes
   * the tab is still promoted on their next visit. The endpoint is idempotent and does its own
   * re-read of the proof, so calling it speculatively is safe.
   */
  useEffect(() => {
    if (!pendingInstagramHandle || !influencerData?.id) return;

    let cancelled = false;
    const influencerId = influencerData.id;
    const promotedHandle = pendingInstagramHandle;

    influencerApi.confirmInstagramHandleChange(influencerId)
      .then(({ promoted }) => {
        if (promoted && !cancelled) {
          toast({
            title: 'Instagram handle updated',
            description: `@${promotedHandle} is now your verified handle`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch(error => console.error('Error confirming Instagram handle change:', error));

    return () => { cancelled = true; };
  }, [pendingInstagramHandle, influencerData?.id, isPendingHandleVerified, toast]);

  /**
   * The reservation was taken over, so the parked handle can never be proven with the code the
   * creator holds. Confirm clears it server-side; without this the pending block would sit there
   * until the code expired. Also reachable while the creator is still on the verification screen,
   * which is where they are most likely to be waiting.
   */
  useEffect(() => {
    if (!hasHandleOwnershipConflict || !pendingInstagramHandle || !influencerData?.id) return;

    const abandonedHandle = pendingInstagramHandle;
    influencerApi.confirmInstagramHandleChange(influencerData.id)
      .then(() => {
        toast({
          title: 'That handle change was interrupted',
          description: `Someone else started verifying @${abandonedHandle}. Your handle is unchanged — start again to get a new code.`,
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
      })
      .catch(error => console.error('Error clearing an interrupted handle change:', error));
  }, [hasHandleOwnershipConflict, pendingInstagramHandle, influencerData?.id, toast]);

  /**
   * Starts an email change. The backend validates and parks the address; Firebase sends the
   * verification mail, because verifyBeforeUpdateEmail needs the signed-in user object.
   */
  const handleRequestEmailChange = async () => {
    if (!influencerData?.id || !user || !newEmail.trim()) return;

    setIsRequestingEmailChange(true);
    try {
      const { pendingEmail } = await influencerApi.requestEmailChange(newEmail, influencerData.id);

      // Scoped to the Firebase call on purpose. A requestEmailChange rejection (bad address,
      // 409) must leave any change already in flight alone — cancelling on it wiped a pending
      // address whose verification link was already live, which is unrecoverable.
      try {
        await verifyBeforeUpdateEmail(user, pendingEmail, {
          url: `${window.location.origin}/profile`,
          handleCodeInApp: false,
        });
      } catch (verifyError) {
        // Stop showing the change. The backend keeps the record regardless — it cannot verify
        // that no link went out — so the address stays reserved until it expires.
        await influencerApi.cancelEmailChange(influencerData.id).catch(() => undefined);
        throw verifyError;
      }

      setNewEmail('');
      toast({
        title: 'Check your inbox',
        description: `Open the link we sent to ${pendingEmail} to finish the change`,
        status: 'info',
        duration: 8000,
        isClosable: true,
      });
    } catch (error) {
      // Firebase requires a recent sign-in before it will move an account's email. Phone-OTP
      // sessions are long lived, so this is a normal outcome rather than a failure.
      const isStaleLogin = (error as { code?: string })?.code === 'auth/requires-recent-login';
      toast({
        title: isStaleLogin ? 'Please sign in again first' : "Couldn't start the email change",
        description: isStaleLogin
          ? 'For your security, sign out and back in, then try changing your email again.'
          : error instanceof Error ? error.message : 'Please try again',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsRequestingEmailChange(false);
    }
  };

  const handleCancelEmailChange = async () => {
    if (!influencerData?.id) return;
    try {
      await influencerApi.cancelEmailChange(influencerData.id);
    } catch (error) {
      toast({
        title: "Couldn't cancel the email change",
        description: error instanceof Error ? error.message : 'Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Firebase gives no callback when the creator opens the verification link, so ask the backend
   * to reconcile whenever a change is outstanding. On success force an ID token refresh: the
   * cached token still carries the old email claim, and the backend authorises email-link
   * sessions by matching that claim against the profile.
   */
  useEffect(() => {
    if (!influencerData?.pendingEmail || !influencerData?.id) return;

    const influencerId = influencerData.id;
    influencerApi.syncEmailChange(influencerId)
      .then(async ({ committed, email_address: committedEmail }) => {
        if (!committed) return;
        await auth.currentUser?.getIdToken(true);
        toast({
          title: 'Email updated',
          description: `${committedEmail} is now your email address`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      })
      .catch(error => console.error('Error syncing email change:', error));
  }, [influencerData?.pendingEmail, influencerData?.id, toast]);

  const handleCancelConfirm = () => {
    setIsCancelModalOpen(false);
    setEditingSection(null);
  };

  const handleSectionEdit = (section: string) => {
    setEditingSection(section);
  };

  const calculateTotalEarned = () => {
    const collabEarned = collabData
      .filter(collab => collab.status === 'collabCompleted')
      .reduce((total, collab) => {
        const campaign = campaignData[collab.id];
        let value = (collab.cash ?? 0) + (collab.credit ?? 0);
        const productValue = campaign?.productValue ?? 0;
        if (productValue > 0 && (isRetailCampaignType(campaign?.campaignType) || isShippingCampaignType(campaign?.campaignType))) {
          value += productValue;
        }
        return total + value;
      }, 0);

    return collabEarned + getTotalBountyEarnings(claims);
  };

  const calculateCreatorScore = () => {
    return 100; // Placeholder
  };

  const formatLocation = (loc: InfluencerLocation) =>
    loc.country === 'United States'
      ? `${loc.city}${loc.state ? `, ${loc.state}` : ''}`
      : `${loc.city}${loc.country ? `, ${loc.country}` : ''}`;

  const socialHandle = hasValidInstagramHandle(influencerData)
    ? `@${influencerData?.instagramHandle}`
    : hasValidTikTokHandle(influencerData)
      ? `@${influencerData?.tiktokHandle}`
      : undefined;
  const nicheNames = (influencerData?.associatedTags ?? [])
    .filter(tagId => associatedTagsContext[tagId])
    .map(tagId => associatedTagsContext[tagId].tag)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
  const collabPreferenceLabel =
    influencerData?.collabPreference === 'cashPreferred' ? 'Cash Preferred'
    : influencerData?.collabPreference === 'cashOnly' ? 'Cash Only'
    : influencerData?.collabPreference === 'all' ? 'All'
    : undefined;
  const completedCollabCount = collabData.filter(c => c.status === 'collabCompleted').length;
  const completeness = getProfileCompleteness(influencerData, influencerLocations, isInstagramVerified);
  // Helper function to get section title
  const getSectionTitle = (section: string | null): string => {
    switch (section) {
      case 'personal-information': return 'Personal information';
      case 'niches': return 'Niches';
      case 'socials': return 'Social accounts';
      case 'locations': return 'Locations';
      case 'collaboration-preferences': return 'Collaboration preferences';
      case 'rates': return 'Rates';
      case 'media-kit': return 'Media kit';
      case 'notifications': return 'Notifications';
      case 'ugc': return 'UGC';
      case 'local-creators': return 'Local Creators';
      case 'wallet': return 'Wallet';
      default: return 'Edit';
    }
  };

  const handleBackFromEdit = () => {
    setEditingSection(null);
  };

  return (
    <Box
      w="100%"
      maxW="100%"
      mx="auto"
      pb={editingSection === 'wallet' ? 0 : 4}
      px={editingSection === 'wallet' ? 0 : { base: 0, md: 6 }}
      bg={editingSection === 'wallet' ? walletColors.pageBg : undefined}
      minH={editingSection === 'wallet' ? '100svh' : undefined}
    >
      {!editingSection ? (
        <Box fontFamily="display" color={ff.ink} lineHeight="normal" bg="white" px="20px" pt="24px" pb={navClearance}>
          <Flex direction="column" gap="28px" maxW="600px" mx="auto">
            {/* Header */}
            <Flex align="flex-end" justify="space-between">
              <Text as="h1" m={0} textStyle="display" letterSpacing="normal" fontSize="29px">
                Profile
              </Text>
              <Box
                as="button"
                aria-label="Support"
                onClick={openCrisp}
                w="44px"
                h="44px"
                borderRadius="full"
                border="1.5px solid"
                borderColor={ff.ink}
                bg="white"
                color={ff.ink}
                fontSize="18px"
                fontWeight={600}
              >
                ?
              </Box>
            </Flex>

            {/* Identity */}
            <Flex align="center" gap="16px">
              <Box position="relative" flex="none" w="84px" h="84px">
                <Avatar
                  src={influencerData?.profile_pic_url}
                  name={influencerData?.firstName}
                  boxSize="84px"
                  bg="#E7DED3"
                  color={ff.ink}
                  sx={{ '& > div': { fontSize: '28px', fontWeight: 600 } }}
                />
                {influencerData?.isDiamond && (
                  <Box
                    position="absolute"
                    right="-4px"
                    bottom="-4px"
                    w="34px"
                    h="34px"
                    sx={{ '& > [role=button]': { bg: ff.ink, color: ff.lime, boxShadow: `0 0 0 1.5px ${ff.lime}` } }}
                  >
                    <DiamondAvatarBadge onClick={openDiamondModal} />
                  </Box>
                )}
              </Box>
              <Flex direction="column" gap="6px" minW={0}>
                <Flex align="center" gap="8px" minW={0}>
                  <Text textStyle="display" letterSpacing="normal" fontSize="34px" noOfLines={1}>
                    {getInfluencerDisplayName(influencerData)}
                  </Text>
                  {influencerData?.isDiamond && (
                    <Flex
                      as="span"
                      flex="none"
                      h="26px"
                      pl="7px"
                      pr="10px"
                      borderRadius="full"
                      bg={ff.ink}
                      color={ff.lime}
                      align="center"
                      gap="5px"
                      fontSize="11px"
                      fontWeight={600}
                      letterSpacing="0.1em"
                      textTransform="uppercase"
                    >
                      <DiamondGlyph size={14} />
                      Diamond
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Flex>

            {/* Stats */}
            <Grid templateColumns="repeat(3, minmax(0, 1fr))" borderRadius="22px" bg={ff.limeSurface} py="18px">
              {[
                { value: completedCollabCount, label: 'Collabs' },
                { value: formatEarningsTile(calculateTotalEarned()), label: 'Earned' },
                { value: calculateCreatorScore(), label: 'Score' },
              ].map((s, i) => (
                <Flex key={s.label} direction="column" gap="6px" px="16px" borderLeft={i ? '1px solid #E0E0DD' : undefined}>
                  <Text fontSize="32px" lineHeight="1.12" letterSpacing="-0.02em" fontWeight={600} noOfLines={1}>
                    {s.value}
                  </Text>
                  <Text fontSize="11px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2}>
                    {s.label}
                  </Text>
                </Flex>
              ))}
            </Grid>

            <ProfileWhopWalletCard
              influencerId={influencerData?.id}
              whopCompanyId={influencerData?.whopCompanyId}
              onOpenWallet={() => handleSectionEdit('wallet')}
            />

            {influencerData?.hasMarketplaceAccess && (
              <Box as="section" display="flex" flexDirection="column">
                <Flex align="center" justify="space-between" mb="6px">
                  <Flex align="center" gap="8px">
                    <LocalCreatorsBrandMark size={22} />
                    <Text as="h2" m={0} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase">
                      Local Creators
                    </Text>
                  </Flex>
                  <Flex as="span" h="24px" px="9px" borderRadius="full" bg={ff.lime} align="center" fontSize="11px" fontWeight={600} letterSpacing="0.08em">
                    TOP 1% CREATOR
                  </Flex>
                </Flex>
                <Text m={0} mb="8px" fontSize="13px" lineHeight="1.4" color={ff.text2}>
                  Your creator page, link and packages for brand bookings.
                </Text>
                <ProfileRow
                  icon={<><rect x="3.5" y="4" width="17" height="16" rx="2" /><path d="M3.5 9h17" /></>}
                  title="Your creator page"
                  value="Link, packages"
                  onClick={() => handleSectionEdit('local-creators')}
                />
              </Box>
            )}

            {completeness.next && (
              <Flex direction="column" gap="12px" p="16px" borderRadius="20px" bg={ff.grey}>
                <Flex align="baseline" justify="space-between">
                  <Text fontSize="15px" fontWeight={600}>Finish your profile</Text>
                  <Text fontSize="13px" color={ff.text2}>{completeness.total - completeness.done} left</Text>
                </Flex>
                <Grid
                  templateColumns={`repeat(${completeness.total}, minmax(0, 1fr))`}
                  gap="4px"
                  role="progressbar"
                  aria-label="Profile completeness"
                  aria-valuemin={0}
                  aria-valuemax={completeness.total}
                  aria-valuenow={completeness.done}
                >
                  {Array.from({ length: completeness.total }, (_, i) => (
                    <Box key={i} h="6px" borderRadius="3px" bg={i < completeness.done ? ff.ink : '#D6D6D3'} />
                  ))}
                </Grid>
                <Box
                  as="button"
                  bg="transparent"
                  textAlign="left"
                  fontSize="13px"
                  lineHeight="1.4"
                  color={ff.body}
                  onClick={() => completeness.next && handleSectionEdit(completeness.next.section)}
                >
                  Brands are more likely to accept you when your profile is complete. Next:{' '}
                  <Text as="span" fontWeight={600} color={ff.ink} textDecoration="underline">
                    {completeness.next.label}
                  </Text>
                </Box>
              </Flex>
            )}

            <ProfileGroup title="Account">
              <ProfileRow
                icon={<><circle cx="12" cy="8" r="4" /><path d="M4 20.5a8 8 0 0 1 16 0" /></>}
                title="Personal info"
                value={getInfluencerDisplayName(influencerData)}
                onClick={() => handleSectionEdit('personal-information')}
              />
              <ProfileRow
                icon={<><path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>}
                title="Locations"
                value={influencerLocations?.length > 0 ? influencerLocations.map(formatLocation).join(', ') : undefined}
                action="Add"
                onClick={() => handleSectionEdit('locations')}
              />
              <ProfileRow
                icon={<><circle cx="12" cy="12" r="4" /><path d="M16 12v1.5a2.5 2.5 0 0 0 5 0V12a9 9 0 1 0-3.5 7.1" /></>}
                title="Social accounts"
                value={socialHandle}
                action="Add"
                onClick={() => handleSectionEdit('socials')}
              />
              <ProfileRow
                icon={<><path d="M4 7a2 2 0 0 1 2-2h12v4" /><path d="M4 7v11a2 2 0 0 0 2 2h14V9H6a2 2 0 0 1-2-2z" /><path d="M16.5 14.5h.01" /></>}
                title="Payout account"
                value={influencerData?.whopCompanyId ? 'Connected' : undefined}
                action="Set up"
                onClick={() => handleSectionEdit('wallet')}
              />
              <ProfileRow
                icon={<><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20.5h4" /></>}
                title="Notifications"
                onClick={() => handleSectionEdit('notifications')}
              />
            </ProfileGroup>

            {isInstagramVerified === false && hasValidInstagramHandle(influencerData) && (
              <Flex role="alert" align="center" gap="12px" p="16px" borderRadius="20px" border="1.5px solid" borderColor={ff.ink}>
                <Box flex="1">
                  <Text fontSize="15px" fontWeight={600}>Instagram not verified</Text>
                  <Text fontSize="13px" lineHeight="1.4" color={ff.text2}>
                    Verify your account to increase visibility to brands.
                  </Text>
                </Box>
                <Box
                  as="button"
                  onClick={handleVerifyInstagram}
                  h="36px"
                  px="14px"
                  borderRadius="full"
                  bg={ff.lime}
                  color={ff.ink}
                  fontSize="13px"
                  fontWeight={600}
                >
                  Verify
                </Box>
              </Flex>
            )}

            <ProfileGroup title="Creator profile" last>
              <ProfileRow
                icon={<><rect x="3" y="6" width="13" height="12" rx="2" /><path d="m16 10 5-3v10l-5-3" /></>}
                title="UGC portfolio"
                onClick={() => handleSectionEdit('ugc')}
              />
              <ProfileRow
                icon={<><path d="M3.5 12.5V4.5h8l9 9-8 8z" /><path d="M8 9h.01" /></>}
                title="Niches"
                value={nicheNames || undefined}
                action="Add"
                onClick={() => handleSectionEdit('niches')}
              />
              <ProfileRow
                icon={<><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></>}
                title="Collab preferences"
                value={collabPreferenceLabel}
                action="Set"
                onClick={() => handleSectionEdit('collaboration-preferences')}
              />
              <ProfileRow
                icon={<><circle cx="12" cy="12" r="8.5" /><path d="M14.8 9.2c-.5-.8-1.5-1.2-2.8-1.2-1.6 0-2.8.8-2.8 2s1.2 1.7 2.8 2 2.8.8 2.8 2-1.2 2-2.8 2c-1.3 0-2.3-.5-2.8-1.3M12 6.5V8M12 16v1.5" /></>}
                title="Rates"
                value={influencerData?.rateRanges ? 'View' : undefined}
                action="Set"
                onClick={() => handleSectionEdit('rates')}
              />
              <ProfileRow
                icon={<><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M9 12h6M9 16h6" /></>}
                title="Media kit"
                value={influencerData?.mediaKit ? 'View' : undefined}
                action="Upload"
                onClick={() => handleSectionEdit('media-kit')}
              />
            </ProfileGroup>


            {canSwitchAccount && (
              isSwitchedAccount ? (
                <Flex align="center" gap="12px" p="16px" borderRadius="20px" bg={ff.grey}>
                  <Box flex="1">
                    <Text fontSize="15px" fontWeight={600}>Viewing switched account</Text>
                    <Text fontSize="13px" lineHeight="1.4" color={ff.text2}>
                      Switch back to your own account.
                    </Text>
                  </Box>
                  <Box as="button" onClick={resetToOwnAccount} h="36px" px="14px" borderRadius="full" bg={ff.ink} color="white" fontSize="13px" fontWeight={600}>
                    Switch Back
                  </Box>
                </Flex>
              ) : (
                <Box
                  as="button"
                  onClick={() => setIsSwitchModalOpen(true)}
                  h="50px"
                  borderRadius="14px"
                  border="1.5px solid"
                  borderColor={ff.ink}
                  bg="white"
                  fontSize="15px"
                  fontWeight={600}
                >
                  Switch Account
                </Box>
              )
            )}

            <Box
              as="button"
              onClick={handleSignOut}
              h="50px"
              borderRadius="14px"
              bg={ff.ink}
              color="white"
              fontSize="15px"
              fontWeight={600}
            >
              Sign Out
            </Box>
          </Flex>
        </Box>
      ) : (
        // Full-width edit view when a section is being edited
        <>
        {/* Header */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          mb={editingSection === 'wallet' ? 0 : { base: 4, md: 6 }}
          position="relative"
          borderBottom={editingSection === 'wallet' ? 'none' : '1px solid'}
          borderColor={editingSection === 'wallet' ? walletColors.cardBorder : ff.line}
          bg={editingSection === 'wallet' ? walletColors.pageBg : undefined}
          py={{ base: 1, md: '20px' }}
        >
          <IconButton
            icon={<ArrowBackIcon />}
            size="lg"
            aria-label="Go Back"
            variant="ghost"
            borderRadius="full"
            onClick={handleBackFromEdit}
            flexShrink={0}
            zIndex={1}
          />
          {editingSection === 'local-creators' ? (
            <Flex
              position="absolute"
              left={0}
              right={0}
              justify="center"
              pointerEvents="none"
            >
              <LocalCreatorsBannerLogo height={32} />
            </Flex>
          ) : (
            <Text
              textStyle="display"
              fontSize={{ base: '20px', md: '24px' }}
              color={ff.ink}
              textAlign="center"
              flex="1"
              px={4}
            >
              {getSectionTitle(editingSection)}
            </Text>
          )}
          <FrostedSupportButton onClick={openCrisp} />
        </Flex>

          {editingSection === 'wallet' ? (
            <WalletPageWithPayoutPortal embedded />
          ) : (
          <Box bg="white" px={{base: 5, md: 8}} py={2} maxW="800px" mx="auto">
            {editingSection === 'personal-information' ? (
            <PersonalInformationEdit
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              email_address={email_address}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              /* A cancelled change keeps its record so a late link click can still be reconciled.
                 It has to stay visible: the address remains reserved and the backend refuses a
                 new one until it expires, so hiding it left the creator with an enabled field
                 and an unexplained rejection. */
              pendingEmail={influencerData?.pendingEmail}
              pendingEmailCancelled={!!influencerData?.pendingEmailCancelledAt}
              canChangeEmail={!isSwitchedAccount}
              isRequestingEmailChange={isRequestingEmailChange}
              onRequestEmailChange={handleRequestEmailChange}
              onCancelEmailChange={handleCancelEmailChange}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              birthday={birthday}
              setBirthday={setBirthday}
              gender={gender}
              setGender={setGender}
              pronouns={Array.from(pronouns)}
              setPronouns={setPronouns}
              ethnicity={ethnicity}
              setEthnicity={setEthnicity}
              languages={languages}
              setLanguages={setLanguages}
              lastSavedPersonalInfo={lastSavedPersonalInfo}
              onFieldSaved={(fieldKey, value) => {
                if (lastSavedPersonalInfo) {
                  setLastSavedPersonalInfo({
                    ...lastSavedPersonalInfo,
                    [fieldKey]: value,
                  });
                }
              }}
              onBack={handleBackFromEdit}
            />
          ) : editingSection === 'niches' ? (
            <NichesEdit
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              collabPreference={collabPreference}
              lastSavedNichesInfo={lastSavedNichesInfo}
              onNichesSaved={(newTags) => {
                if (lastSavedNichesInfo) {
                  setLastSavedNichesInfo({ selectedTags: newTags });
                }
              }}
              onBack={handleBackFromEdit}
            />
          ) : editingSection === 'socials' ? (
            <SocialsEdit
              instagramHandle={instagramHandle}
              newInstagramHandle={newInstagramHandle}
              setNewInstagramHandle={setNewInstagramHandle}
              pendingInstagramHandle={pendingInstagramHandle}
              pendingHandleExpiresAt={pendingHandleExpiresAt}
              isRequestingHandleChange={isRequestingHandleChange}
              onRequestHandleChange={handleRequestInstagramHandleChange}
              onCancelHandleChange={handleCancelInstagramHandleChange}
              isCheckingInstagramVerified={isCheckingInstagramVerified}
              tiktokHandle={tiktokHandle}
              setTiktokHandle={setTiktokHandle}
              youtubeHandle={youtubeHandle}
              setYoutubeHandle={setYoutubeHandle}
              pinterestHandle={pinterestHandle}
              setPinterestHandle={setPinterestHandle}
              snapchatHandle={snapchatHandle}
              setSnapchatHandle={setSnapchatHandle}
              rateRanges={rateRanges}
              setRateRanges={setRateRanges}
              lastSavedSocialsInfo={lastSavedSocialsInfo}
              onSocialsSaved={(field, value) => {
                if (lastSavedSocialsInfo) {
                  setLastSavedSocialsInfo({
                    ...lastSavedSocialsInfo,
                    [field]: value,
                  });
                }
              }}
              onBack={handleBackFromEdit}
              isInstagramVerified={isInstagramVerified}
              onVerifyInstagram={handleVerifyInstagram}
            />
          ) : editingSection === 'locations' ? (
            <LocationsEdit
              locations={locations}
              setLocations={setLocations}
              lastSavedLocationsInfo={lastSavedLocationsInfo}
              onLocationsSaved={(newLocations) => {
                if (lastSavedLocationsInfo) {
                  setLastSavedLocationsInfo({
                    ...lastSavedLocationsInfo,
                    locations: newLocations,
                  });
                }
              }}
              onShippingAddressSaved={(newAddress) => {
                if (lastSavedLocationsInfo) {
                  setLastSavedLocationsInfo({
                    ...lastSavedLocationsInfo,
                    defaultShippingAddress: newAddress,
                  });
                }
              }}
              onBack={handleBackFromEdit}
            />
          ) : editingSection === 'collaboration-preferences' ? (
            <CollaborationPreferencesEdit
              collabPreference={collabPreference}
              setCollabPreference={setCollabPreference}
              associatedTags={selectedTags}
              lastSavedCollabPreferences={lastSavedCollabPreferences}
              onCollabPreferencesSaved={(newPreference) => {
                if (lastSavedCollabPreferences) {
                  setLastSavedCollabPreferences({ collabPreference: newPreference });
                }
              }}
              onBack={handleBackFromEdit}
            />
          ) : editingSection === 'rates' ? (
            <RatesEdit
              rateRanges={rateRanges}
              setRateRanges={setRateRanges}
              bundlePricing={bundlePricing}
              setBundlePricing={setBundlePricing}
              collabPreference={collabPreference}
              lastSavedRatesInfo={lastSavedRatesInfo}
              onRatesSaved={(newRateRanges, newBundlePricing) => {
                if (lastSavedRatesInfo) {
                  setLastSavedRatesInfo({
                    rateRanges: newRateRanges,
                    bundlePricing: newBundlePricing,
                  });
                }
              }}
              onBack={handleBackFromEdit}
            />
          ) : editingSection === 'media-kit' ? (
            <MediaKitEdit
              mediaKit={mediaKit}
              setMediaKit={setMediaKit}
              lastSavedMediaKit={lastSavedMediaKit}
              onMediaKitSaved={(newMediaKit) => {
                setLastSavedMediaKit(newMediaKit);
              }}
              onBack={handleBackFromEdit}
            />
          ) : editingSection === 'ugc' ? (
            <UgcEdit
              onBack={handleBackFromEdit}
            />
          ) : editingSection === 'notifications' ? (
            <NotificationsEdit
              onBack={handleBackFromEdit}
            />
          ) : editingSection === 'local-creators' ? (
            <LocalCreatorsEdit
              onBack={handleBackFromEdit}
            />
          ) : null}
          </Box>
          )}
        </>
      )}

      {/* Modals */}
      <DiamondTierModal isOpen={isCelebrationOpen} onClose={dismissCelebration} />

      <WalletPayoutIntroModal
        isOpen={isIntroOpen}
        onClose={dismissIntro}
        onSetup={handleIntroSetup}
        isSetupLoading={isIntroSetupLoading}
      />

      {isCancelModalOpen && (
        <CancelChangesModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelConfirm}
        />
      )}

      {canSwitchAccount && (
        <SwitchAccountModal
          isOpen={isSwitchModalOpen}
          onClose={() => setIsSwitchModalOpen(false)}
          onSwitchAccount={switchToInfluencer}
        />
      )}
    </Box>
  );
};

export default ProfilePage;